// Single source of truth for reading/writing the current tenant slug.
//
// Storage strategy (plan item #27): the live selection lives in
// ``sessionStorage`` so two tabs can have two different tenants open at
// once without bleeding into each other. We mirror the most recent
// selection into ``localStorage`` so opening a fresh tab still lands on
// the user's last choice instead of nothing.

const TENANT_KEY = 'revpulse_tenant';

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

export function readTenantSlug(): string | null {
  if (!isBrowser()) return null;
  return (
    sessionStorage.getItem(TENANT_KEY) ?? localStorage.getItem(TENANT_KEY) ?? null
  );
}

export function writeTenantSlug(slug: string): void {
  if (!isBrowser()) return;
  sessionStorage.setItem(TENANT_KEY, slug);
  localStorage.setItem(TENANT_KEY, slug);
}

export function clearTenantSlug(): void {
  if (!isBrowser()) return;
  sessionStorage.removeItem(TENANT_KEY);
  localStorage.removeItem(TENANT_KEY);
}

// Display-only cache of the tenant switcher list (slug + name, entity info).
// Read while /tenants/ is still in flight so the header switcher and the
// breadcrumb render the same thing before and after the fetch — never used
// to validate a slug or pick the active tenant (TenantContext only trusts the
// live list for that).
const TENANT_LIST_KEY = 'revpulse_tenant_list';

export interface CachedTenant {
  id: string;
  name: string;
  slug: string;
  isGroup?: boolean;
  group_slug?: string | null;
  group_name?: string | null;
}

export function readCachedTenants(): CachedTenant[] {
  if (!isBrowser()) return [];
  try {
    const raw = localStorage.getItem(TENANT_LIST_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (t): t is CachedTenant =>
        !!t && typeof t === 'object' && typeof t.slug === 'string' && typeof t.name === 'string',
    );
  } catch {
    return [];
  }
}

export function writeCachedTenants(tenants: CachedTenant[]): void {
  if (!isBrowser()) return;
  try {
    localStorage.setItem(TENANT_LIST_KEY, JSON.stringify(tenants));
  } catch {
    /* storage unavailable: the switcher just waits for the fetch */
  }
}

// Last known admin flag per signed-in account (keyed by the MSAL username).
// Display-only, like the tenant list above: the sidebar renders the admin
// entries (Upload, Admin, Feedback inbox) from it while /me is in flight, so
// they don't vanish and come back on every cold load. Never an access gate —
// AdminGate, the admin/settings layouts and the API decide off /me.
const ROLE_KEY_PREFIX = 'revpulse_is_admin:';

export function readCachedIsAdmin(userKey: string): boolean | null {
  if (!isBrowser() || !userKey) return null;
  try {
    const raw = localStorage.getItem(ROLE_KEY_PREFIX + userKey.toLowerCase());
    return raw === '1' ? true : raw === '0' ? false : null;
  } catch {
    return null;
  }
}

export function writeCachedIsAdmin(userKey: string, isAdmin: boolean): void {
  if (!isBrowser() || !userKey) return;
  try {
    localStorage.setItem(ROLE_KEY_PREFIX + userKey.toLowerCase(), isAdmin ? '1' : '0');
  } catch {
    /* storage unavailable: the sidebar shows placeholders until /me lands */
  }
}

// "alkemy-commerce" → "Alkemy Commerce". Last-resort label when neither the
// live nor the cached tenant list knows the slug yet.
export function humanizeSlug(slug: string): string {
  return slug
    .split(/[-_]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}
